/*
 * Capture module — real backend in place (capture_forms +
 * capture_submissions tables, /api/capture/{forms,submissions}). Each
 * sub-view is a thin slice over the shared data: dashboard summary,
 * form list, submission list, etc. The full form-builder + workflow
 * polish lands in a follow-up; this MVP makes the IA functional.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import StatusBadge from "../components/StatusBadge.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Form = Awaited<ReturnType<typeof api.listCaptureForms>>["forms"][number];
type Submission = Awaited<ReturnType<typeof api.listCaptureSubmissions>>["submissions"][number];

export interface CaptureModuleProps {
  view: "dashboard" | "forms" | "builder" | "drafts" | "submissions" | "review" | "analytics" | "settings";
}

export default function CaptureModule({ view }: CaptureModuleProps) {
  const [forms, setForms] = useState<Form[] | null>(null);
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.listCaptureForms(), api.listCaptureSubmissions()])
      .then(([f, s]) => { setForms(f.forms); setSubmissions(s.submissions); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const formsByStatus = useMemo(() => {
    const m = { draft: 0, published: 0, archived: 0 };
    for (const f of forms ?? []) m[(f.status as keyof typeof m)] = (m[(f.status as keyof typeof m)] ?? 0) + 1;
    return m;
  }, [forms]);

  const subsByStatus = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of submissions ?? []) m[s.status] = (m[s.status] ?? 0) + 1;
    return m;
  }, [submissions]);

  const reviewQueue = useMemo(() => (submissions ?? []).filter((s) => s.status === "submitted" || s.status === "under-review"), [submissions]);

  const TITLES: Record<string, string> = {
    dashboard: "Capture dashboard",
    forms: "Capture forms",
    builder: "Form builder",
    drafts: "Drafts",
    submissions: "Submissions",
    review: "Review queue",
    analytics: "Capture analytics",
    settings: "Capture settings"
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{TITLES[view]}</h1>
          <p className="muted">TallyStream-local capture module — workspace-scoped forms and submissions backed by Postgres.</p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {view === "dashboard" && (
        <>
          <section className="stat-grid">
            <div className="stat"><div className="stat-label">Forms</div><div className="stat-value">{forms?.length ?? "—"}</div><div className="stat-hint">{formsByStatus.published} published · {formsByStatus.draft} draft</div></div>
            <div className="stat"><div className="stat-label">Submissions</div><div className="stat-value">{submissions?.length ?? "—"}</div></div>
            <div className="stat"><div className="stat-label">Review queue</div><div className="stat-value">{reviewQueue.length}</div></div>
            <div className="stat"><div className="stat-label">Approved</div><div className="stat-value">{subsByStatus.approved ?? 0}</div></div>
          </section>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Link to="/app/capture/forms" className="btn btn-secondary">Open forms</Link>
            <Link to="/app/capture/submissions" className="btn btn-secondary">View submissions</Link>
            <Link to="/app/capture/review" className="btn btn-primary">Review queue ({reviewQueue.length})</Link>
          </div>
        </>
      )}

      {view === "forms" && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Link to="/app/capture/forms/new" className="btn btn-primary">New form</Link>
          </div>
          {forms === null && <SkeletonTable rows={4} columns={4} />}
          {forms !== null && forms.length === 0 && (
            <div className="empty-state-card">
              <h2 style={{ marginTop: 0 }}>No forms yet</h2>
              <p>Create your first capture form to start collecting data inside TallyStream.</p>
            </div>
          )}
          {forms && forms.length > 0 && (
            <table className="transfer-table">
              <thead><tr><th>Name</th><th>Domain</th><th>Status</th><th>Updated</th></tr></thead>
              <tbody>
                {forms.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{f.name}</div>
                      {f.description && <div className="muted" style={{ fontSize: 12 }}>{f.description}</div>}
                    </td>
                    <td><span className="pill pill-muted">{f.domain}</span></td>
                    <td><StatusBadge status={f.status} /></td>
                    <td className="muted" style={{ fontSize: 12 }}>{new Date(f.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {view === "builder" && (
        <FormBuilder onSaved={() => api.listCaptureForms().then((r) => setForms(r.forms))} />
      )}

      {(view === "submissions" || view === "review" || view === "drafts") && (
        <SubmissionsTable
          submissions={
            view === "review" ? reviewQueue
            : view === "drafts" ? [] // drafts not yet split out; submissions table is the closest
            : submissions
          }
          forms={forms}
          showReviewActions={view === "review"}
          onReview={async (id, status) => {
            await api.reviewCapture(id, status);
            const s = await api.listCaptureSubmissions();
            setSubmissions(s.submissions);
          }}
          emptyMessage={view === "drafts" ? "Draft tracking ships with the next capture release." : "No submissions yet."}
        />
      )}

      {view === "analytics" && (
        <section className="stat-grid">
          {Object.entries(subsByStatus).map(([k, v]) => (
            <div key={k} className="stat"><div className="stat-label">{k}</div><div className="stat-value">{v}</div></div>
          ))}
          {(submissions?.length ?? 0) === 0 && (
            <div className="empty-state-card">
              <h2 style={{ marginTop: 0 }}>No analytics yet</h2>
              <p>Capture analytics populate as submissions land. The full warehouse-backed dashboards are scheduled for a follow-up.</p>
            </div>
          )}
        </section>
      )}

      {view === "settings" && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>Capture settings</h2>
          <p>Settings (default org unit, validation strictness, notification rules) are configured globally for the workspace via the Workspace → Settings page. Form-level overrides land with the Form builder rewrite.</p>
        </div>
      )}
    </div>
  );
}

function FormBuilder({ onSaved }: { onSaved: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState<"aggregate" | "tracker" | "individual">("aggregate");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.upsertCaptureForm({ name, description, domain, status: "draft", fields: [] });
      setNotice(`Form created (${r.id}). Field-level builder ships next; you can already see the form in the catalog.`);
      setName(""); setDescription("");
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <section className="empty-state-card">
      <h2 style={{ marginTop: 0 }}>Create form</h2>
      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
        <label className="transfer-filter">
          Name
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monthly facility report" />
        </label>
        <label className="transfer-filter">
          Domain
          <select value={domain} onChange={(e) => setDomain(e.target.value as typeof domain)}>
            <option value="aggregate">Aggregate</option>
            <option value="tracker">Tracker</option>
            <option value="individual">Individual-level</option>
          </select>
        </label>
        <label className="transfer-filter" style={{ gridColumn: "1 / -1" }}>
          Description
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What the form captures" />
        </label>
      </div>
      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Create form"}
        </button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
        Field-level builder (drag-drop, validation rules, DHIS2 binding) ships in the next capture release. For now, forms are created as empty shells.
      </p>
    </section>
  );
}

function SubmissionsTable({
  submissions,
  forms,
  showReviewActions,
  onReview,
  emptyMessage
}: {
  submissions: Submission[] | null;
  forms: Form[] | null;
  showReviewActions: boolean;
  onReview: (id: string, status: string) => void | Promise<void>;
  emptyMessage: string;
}) {
  const formNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of forms ?? []) m.set(f.id, f.name);
    return m;
  }, [forms]);

  if (submissions === null) return <SkeletonTable rows={4} columns={5} />;
  if (submissions.length === 0) return (
    <div className="empty-state-card">
      <h2 style={{ marginTop: 0 }}>{emptyMessage}</h2>
    </div>
  );
  return (
    <table className="transfer-table">
      <thead>
        <tr>
          <th>Submitted</th>
          <th>Form</th>
          <th>Status</th>
          <th>Submitter</th>
          {showReviewActions && <th></th>}
        </tr>
      </thead>
      <tbody>
        {submissions.map((s) => (
          <tr key={s.id}>
            <td className="muted" style={{ fontSize: 12 }}>{new Date(s.submittedAt).toLocaleString()}</td>
            <td>{formNameById.get(s.formId) ?? <code style={{ fontSize: 11 }}>{s.formId}</code>}</td>
            <td><StatusBadge status={s.status} /></td>
            <td className="mono" style={{ fontSize: 11 }}>{s.submitterUserId ?? "—"}</td>
            {showReviewActions && (
              <td>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void onReview(s.id, "approved")}>Approve</button>{" "}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void onReview(s.id, "rejected")}>Reject</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
